import { sortBy } from "lodash-es";
import type { GenericSize } from "@brickme/project-core/src";
import { pixAPicKit } from "@brickme/project-core/src/pix-a-pic.ts";

const allLayouts = [
	// Single kit
	{ width: 2, height: 2 },

	// Double kit
	{ width: 2, height: 3 },
	{ width: 3, height: 2 },
	{ width: 2, height: 4 },
	{ width: 4, height: 2 },

	// Triple kit
	{ width: 3, height: 3 },
	{ width: 3, height: 4 },
	{ width: 4, height: 3 },

	// Quadruple kit
	{ width: 2, height: 6 },
	{ width: 3, height: 5 },
	{ width: 4, height: 4 },
	{ width: 5, height: 3 },
	{ width: 6, height: 2 },
];

const maxLayoutDimension = Math.max(
	...allLayouts.flatMap(({ width, height }) => [width, height]),
);

const maxConsideredNumberOfKits = 4;

// Preferably we want square as the default layout
function isPreferredLayoutCandidateForDefault({
	width,
	height,
}: GenericSize<number>) {
	return width === height;
}

function getLayoutPermutations(numberOfKits: number) {
	if (numberOfKits < 1) {
		throw new Error("number of kits must be >= 0");
	}

	const calcNumberOfKits = Math.min(numberOfKits, maxConsideredNumberOfKits);
	const previousNumBaseplates =
		(calcNumberOfKits - 1) * pixAPicKit.numberOfBasePlates;
	const numBaseplates = calcNumberOfKits * pixAPicKit.numberOfBasePlates;
	const allAvailableLayouts = allLayouts.filter(({ width, height }) => {
		// Eyal doesn't want single kit options to display if choose 2 kits
		const area = width * height;
		return area > previousNumBaseplates && area <= numBaseplates;
	});

	if (allAvailableLayouts.length === 0) {
		throw new Error(
			`No available layouts for previous ${previousNumBaseplates} and current ${numBaseplates} baseplates`,
		);
	}
	return sortBy(allAvailableLayouts, [
		({ width, height }) => width * height,
		({ width }) => width,
	]);
}

function getDefaultLayout(numberOfKits: number) {
	const layoutPermutations = getLayoutPermutations(numberOfKits);
	const squarePermutations = layoutPermutations.filter(
		isPreferredLayoutCandidateForDefault,
	);
	if (squarePermutations.length > 0) {
		return squarePermutations[Math.floor((squarePermutations.length - 1) / 2)];
	}

	return layoutPermutations[0];
}

export { maxLayoutDimension, getDefaultLayout, getLayoutPermutations };
